import React, { useMemo } from 'react';
import classNames from 'classnames';
import useScreen from 'hooks/useScreen';
import isNewConsumerApp from 'commons/isNewConsumerApp';
import ImageContentBlock from './components/ImageContentBlock';
import CardCarousel from '../image-slider-standard/components/CardCarousel';
import FixedWidthContainer from '../FixedWidthContainer';
import EmblaCarousel, {
  EmblaSlide,
} from '../image-slider-standard/components/EmblaCarousel';

const MOBILE_SCROLL_TEXT = 'Scroll for more >';
const HIDE_SCROLLBAR_CLASSES =
  'overflow-x-auto snap-x snap-mandatory [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden';
const DEFAULT_BLOCK_SECTION = {};

const GridView = ({ blocks, size, isMobile, columns }) => (
  <>
    {isMobile && columns > 2 && (
      <span
        className={classNames('block w-full text-left text-xs', {
          'ml-2': isNewConsumerApp,
        })}
        aria-live="polite"
      >
        {MOBILE_SCROLL_TEXT}
      </span>
    )}
    <div className="w-full flex">
      <div
        className={classNames('flex flex-row [&>*]:!mt-0', {
          'w-full': size === 'Full Width',
          'max-w-[1400px]': size === 'Full Page',
          'overflow-x-auto snap-x snap-mandatory': isMobile,
          [HIDE_SCROLLBAR_CLASSES]: isMobile,
        })}
      >
        {blocks}
      </div>
    </div>
  </>
);

const CarouselView = ({ blocks }) => (
  <CardCarousel
    navType="Button"
    variant="blog-slider"
    visibleSlides={4}
    fullHeight
  >
    {blocks}
  </CardCarousel>
);

const MultipleBlocks = ({ config }) => {
  const {
    imageComponent: { size = 'Full Width' },
    blockSectionConfig: { columns = 2 },
    blockSection = DEFAULT_BLOCK_SECTION,
  } = config;
  const { isMobile } = useScreen();
  const blocksArray = useMemo(
    () =>
      Object.values(blockSection)
        .slice(0, columns)
        .filter(Boolean),
    [blockSection, columns]
  );
  const canScroll = (!isMobile && columns > 4) || (isMobile && columns > 2);
  const hasGaps = !isMobile && columns > 3;

  const blocks = useMemo(
    () =>
      blocksArray.map((blockConfig, index) =>
        isNewConsumerApp ? (
          <EmblaSlide
            className={classNames('basis-1/2', {
              '!basis-1/3': !isMobile && columns === 3,
              '!basis-1/4': !isMobile && columns >= 4,
              '!pl-0': !hasGaps,
            })}
            key={`blockSection-${blockConfig?.title?.text}-${
              blockConfig?.body?.text
            }-${index + 1}`}
          >
            <ImageContentBlock blockConfig={blockConfig} />
          </EmblaSlide>
        ) : (
          <div
            className={classNames('min-w-[50%] md:min-w-[25%]', {
              'px-2': isNewConsumerApp,
            })}
            key={`blockSection-${blockConfig?.title?.text}-${
              blockConfig?.body?.text
            }-${index + 1}`}
          >
            <ImageContentBlock blockConfig={blockConfig} />
          </div>
        )
      ),
    [blocksArray, columns, hasGaps, isMobile]
  );

  const showCarousel = columns >= 4 && !isMobile;

  return (
    <FixedWidthContainer
      className={classNames({
        'w-full max-w-none': size === 'Full Width',
        'max-w-[1400px]': size === 'Full Page',
      })}
    >
      {!isNewConsumerApp &&
        (showCarousel ? (
          <CarouselView blocks={blocks} />
        ) : (
          <GridView
            blocks={blocks}
            size={size}
            isMobile={isMobile}
            columns={columns}
          />
        ))}
      {isNewConsumerApp && (
        <EmblaCarousel
          className={classNames({
            'pointer-events-none': !canScroll,
            '[&_.embla-container]:!-ml-0': !hasGaps,
          })}
          variant="blog-slider"
          forceHideButtons={columns < 5}
        >
          {blocks}
        </EmblaCarousel>
      )}
    </FixedWidthContainer>
  );
};

export default MultipleBlocks;
